/**
 * describePolyOrder — resolve a Polymarket asset_id (tokenId) into a
 * human-readable market description by scanning marketStore's currently-
 * loaded games. Returns null when no match is found — typically because
 * the relevant sport tab hasn't loaded yet, or the market expired before
 * the page mounted. Callers fall back to a truncated tokenId in that case.
 *
 * Why client-side: hydra's open-orders endpoint returns raw CLOB orders
 * with no market metadata attached — just asset_id, side, price, size.
 * The marketStore already has every loaded poly market's tokenId pair
 * (yesTokenId + noTokenId per team's VenueBook), so a sweep avoids a relay
 * round-trip per order.
 *
 * Two variants with one cache behind them:
 * - describePolyOrder (FRESH): sweeps every call. Order paths (edit/replace)
 *   use this — see its docstring for why stale tickSize/negRisk can destroy
 *   a resting order.
 * - describePolyOrderCached: render-path variant; resolved labels serve from
 *   cache, unresolved ids retry on a wall-clock cadence.
 */

import { useMarketStore } from '@/stores/marketStore';
import { sportForMarketFrame } from '@/stores/marketFrameNormalization';
import type { BookLevel } from '@/types';
import {
  SPORT_TABS,
  type MultiOutcomeEvent,
  type SeriesOutcome,
  type Sport,
  type VenueBook,
} from '@/views/ValueDashboard/types';

export interface PolyOrderDescription {
  /** "OKC @ SAS" — away vs home short codes. */
  matchup: string;
  /** Short code of the team this order's outcome resolves YES for. */
  betTeam: string;
  /** Whether the asset_id matches the team's YES or NO token. With the
   *  per-team Polymarket book convention (each team's row carries its own
   *  YES outcome + the cross-token NO outcome), `'yes'` means "{betTeam}
   *  wins" and `'no'` means "{betTeam} loses". */
  side: 'yes' | 'no';
  sport: Sport;
  /** YYYY-MM-DD as marketStore reports it on the GameRow. */
  date: string;
  /** Market-specific CLOB tick size. Required to safely replace fine-tick orders. */
  tickSize?: string;
  /** Market-specific negRisk flag. Required to safely replace NEG_RISK orders. */
  negRisk?: boolean;
  /** Slug ticker (PM-…) that keys marketStore for this market (Brendan,
   *  Jul 8). Immutable per token, so it's safe to cache — the LIVE data
   *  (last/volume/book) is read fresh by ticker via {@link polyOrderLiveData}
   *  rather than frozen here. Empty string when the slot has no ticker. */
  pmTicker: string;
  /** Hydra's `canonicalGameKeyV2` for this market. Captured HERE, at resolve
   *  time, for the same reason `matchup`/`date` are: hydra prunes a finished
   *  game's frame, so a store lookup goes null on yesterday's market and any key
   *  read live would vanish with it. Riding the persisted cache keeps settled
   *  rows groupable by game. Consumers must still prefer a LIVE getMarket() read
   *  and fall back to this only once the market is gone.
   *
   *  v2 ONLY — deliberately never the legacy 4-seg `canonicalGameKey`. That key
   *  carries no game number, so promoting it silently assumes game 1; a resolved
   *  description is cached AND PERSISTED forever, so a doubleheader's game 2
   *  would be pinned as game 1 and group with the wrong game. Costs nothing:
   *  prod hydra ships zero markets with a legacy key but no v2 (sampled 1717).
   *  Absent on frames carrying no canonical key at all (NHL/NBA/boxing today). */
  canonicalGameKey?: string;
}

function matchToken(assetId: string, book: VenueBook | null | undefined): 'yes' | 'no' | null {
  if (!book) return null;
  if (book.tokenId === assetId) return 'yes';
  if (book.noTokenId === assetId) return 'no';
  return null;
}

function describeMultiOutcomeBook(
  assetId: string,
  sport: Sport,
  event: MultiOutcomeEvent,
  outcome: SeriesOutcome,
): PolyOrderDescription | null {
  const book = outcome.polymarket;
  const side = matchToken(assetId, book);
  if (!side || !book) return null;
  const matchup =
    event.awayCode && event.homeCode
      ? `${event.awayCode} @ ${event.homeCode}`
      : event.title;
  const betTeam = outcome.role === 'draw' ? 'Draw' : outcome.name || outcome.code;
  // Multi-outcome (soccer 3-way) is the one resolve path with no game key in
  // scope: MultiOutcomeEvent.key is the EVENT ticker (KXUECLGAME-…), not a
  // canonical game key — unlike the game-row sweep (GameRow.key is already
  // gameKeyV2) and the frame fallback (the HydraMarket is in scope). Resolve the
  // market explicitly so the same-game key still gets captured into the cache.
  const mkt = useMarketStore.getState().getMarket(book.marketTicker);
  return {
    matchup,
    betTeam,
    side,
    sport,
    date: event.date ?? '',
    tickSize: book.tickSize,
    negRisk: book.negRisk,
    pmTicker: book.marketTicker,
    canonicalGameKey: mkt?.canonicalGameKeyV2,
  };
}

/** How long the CACHED variant waits before re-sweeping for an id that
 *  didn't resolve (wall-clock, NOT store-version-denominated: a quiet feed
 *  stops bumping the version entirely, and an open order's label must never
 *  be hostage to feed activity). 3s keeps late-loading sports snappy while a
 *  dead/foreign assetId can't buy the full 14-sport scan on every render. */
const UNRESOLVED_RETRY_MS = 3_000;

interface DescCacheEntry {
  desc: PolyOrderDescription | null;
  at: number; // performance.now() of the sweep that produced this entry
}
const descCache = new Map<string, DescCacheEntry>();

// ── Persistence: a label resolved ONCE survives forever ─────────
// Hydra prunes finished games from its feed, so an open order/position on
// yesterday's market can become unresolvable live — those rendered as bare
// hex after every refresh (Brendan, Jul-9). Resolved descriptions are
// session-immutable, so persist them: any market this browser EVER labeled
// stays labeled. Only resolved entries persist (nulls retry live); the
// order paths' FRESH tickSize/negRisk reads still come from the live sweep
// and refuse when the market is gone — persistence is display-grade only.
const DESC_PERSIST_KEY = 'g1_poly_desc_cache_v1';
const DESC_PERSIST_CAP = 400;

// ── Market-level (conditionId-keyed) descriptions ────────────────
// The relay's fills/positions ledgers key poly rows by the market
// CONDITION ID (CLOB Trade.market), not a tokenId. A conditionId must
// NEVER be answered from the token-keyed cache above: a full desc carries
// side/betTeam for ONE of the market's two tokens, and pre-existing render
// paths key describePolyOrderCached by `asset_id ?? market` — served a
// sibling token's desc, they'd chip (betTeam) SIDE and bake team/side into
// deep links with the WRONG orientation on a live order (pre-existing
// behavior for a conditionId key is a fail-safe null → hex). Market-level
// lookups therefore get their own map holding only orientation-free
// fields, seeded whenever a token resolution can name its market's
// conditionId, and persisted under a SEPARATE key (the v1 payload's tuples
// are full PolyOrderDescriptions — mixing reduced entries in would feed
// token-keyed callers).
export interface PolyMarketDescription {
  /** "OKC @ SAS" (game-row sweep) or the market's own title (frame path). */
  matchup: string;
  sport: Sport;
  /** Slug ticker (PM-…) for live-data reads; '' when the slot had none. */
  pmTicker: string;
  /** Hydra's `canonicalGameKeyV2`, captured at resolve time for the same reason
   *  `matchup` is persisted: once hydra ages a settled market out of the feed,
   *  `getMarket` goes null and any key read live would vanish with it. Riding
   *  this cache is what lets a settled POSITION — which carries only a
   *  conditionId — still be grouped by game. Consumers must prefer a LIVE
   *  getMarket() read and fall back to this only once the market is gone.
   *  v2 ONLY, never the legacy key — see {@link PolyOrderDescription}. */
  canonicalGameKey?: string;
}

const marketDescCache = new Map<string, PolyMarketDescription>();
const MARKET_DESC_PERSIST_KEY = 'g1_poly_market_desc_cache_v1';

function hydratePersistedDescs(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const raw = localStorage.getItem(DESC_PERSIST_KEY);
    if (raw) {
      const entries = JSON.parse(raw) as Array<[string, PolyOrderDescription]>;
      if (Array.isArray(entries)) {
        for (const [assetId, desc] of entries) {
          if (typeof assetId === 'string' && desc && typeof desc === 'object') {
            // Quarantine conditionId-keyed tuples: tokenIds are decimal
            // strings, conditionIds are 0x-hex, and a FULL desc under a
            // conditionId would hand one token's side/betTeam to callers
            // describing the whole market (the orientation hazard the
            // market-level map exists to prevent). No deployed build wrote
            // such tuples — purely defensive. Salvage the market-level
            // fields instead of dropping the label outright.
            if (assetId.startsWith('0x')) {
              if (!marketDescCache.has(assetId) && typeof desc.matchup === 'string') {
                marketDescCache.set(assetId, {
                  matchup: desc.matchup,
                  sport: desc.sport,
                  pmTicker: desc.pmTicker ?? '',
                  canonicalGameKey: desc.canonicalGameKey,
                });
              }
              continue;
            }
            descCache.set(assetId, { desc, at: 0 });
          }
        }
      }
    }
    const rawMarket = localStorage.getItem(MARKET_DESC_PERSIST_KEY);
    if (rawMarket) {
      const entries = JSON.parse(rawMarket) as Array<[string, PolyMarketDescription]>;
      if (Array.isArray(entries)) {
        for (const [conditionId, desc] of entries) {
          if (typeof conditionId === 'string' && desc && typeof desc === 'object') {
            marketDescCache.set(conditionId, desc);
          }
        }
      }
    }
  } catch {
    /* corrupt/unavailable storage — labels just resolve live */
  }
}
hydratePersistedDescs();

let _persistScheduled = false;
function persistResolvedDescs(): void {
  if (_persistScheduled) return;
  _persistScheduled = true;
  // Coalesce bursts (first render resolving many orders) into one write.
  setTimeout(() => {
    _persistScheduled = false;
    try {
      if (typeof localStorage === 'undefined') return;
      const resolved: Array<[string, PolyOrderDescription]> = [];
      for (const [assetId, entry] of descCache) {
        if (entry.desc) resolved.push([assetId, entry.desc]);
      }
      // Cap: keep the most recent insertions (Map preserves order).
      localStorage.setItem(
        DESC_PERSIST_KEY,
        JSON.stringify(resolved.slice(-DESC_PERSIST_CAP)),
      );
      localStorage.setItem(
        MARKET_DESC_PERSIST_KEY,
        JSON.stringify(Array.from(marketDescCache).slice(-DESC_PERSIST_CAP)),
      );
    } catch {
      /* storage full/blocked — in-memory cache still works */
    }
  }, 250);
}

export function _resetDescribePolyOrderCacheForTests(): void {
  descCache.clear();
  marketDescCache.clear();
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(DESC_PERSIST_KEY);
      localStorage.removeItem(MARKET_DESC_PERSIST_KEY);
    }
  } catch {
    /* ignore */
  }
}

/**
 * FRESH variant — always sweeps the store, then write-through-caches the
 * result (so the cached variant picks up resolutions immediately).
 *
 * ORDER PATHS MUST USE THIS ONE. tickSize/negRisk are NOT session-immutable
 * (hydra re-runs Gamma discovery every ~120s and can change a market's tick
 * regime), and the Polymarket replace flow is non-atomic cancel-then-place —
 * a stale tickSize/negRisk there gets the resting order cancelled and the
 * re-place rejected. A fresh sweep reads the store's ≤120s-fresh values.
 */
export function describePolyOrder(assetId: string): PolyOrderDescription | null {
  if (!assetId) return null;
  const store = useMarketStore.getState();
  const desc = sweepForAssetId(assetId, store);
  // Never clobber a resolved (possibly persisted) entry with a miss — a
  // market hydra has pruned would otherwise lose its label the first time
  // an order path re-sweeps it. The FRESH caller still gets the truthful
  // null (order paths must refuse on a gone market); only the render
  // cache keeps the historical label.
  const existing = descCache.get(assetId);
  if (desc !== null || !existing?.desc) {
    descCache.set(assetId, { desc, at: performance.now() });
    if (desc !== null) {
      // Seed the market-level map too: once hydra ages a settled market out
      // of the feed, the conditionId is the only id the ledger rows still
      // carry. Reduced to orientation-free fields — the resolving token's
      // side/betTeam must never answer a conditionId lookup.
      const conditionId = desc.pmTicker
        ? store.getMarket(desc.pmTicker)?.conditionId
        : undefined;
      if (conditionId && !marketDescCache.has(conditionId)) {
        marketDescCache.set(conditionId, {
          matchup: desc.matchup,
          sport: desc.sport,
          pmTicker: desc.pmTicker,
          canonicalGameKey: desc.canonicalGameKey,
        });
      }
      persistResolvedDescs();
    }
  }
  return desc;
}

/**
 * Market-level render lookup for LEDGER-keyed ids (fills/positions rows,
 * keyed by conditionId). Orientation-free by construction — see the
 * marketDescCache doc. O(1) end to end: cache first, then the store's
 * conditionId index (live markets resolve instantly and seed the cache).
 * NEVER sweepForAssetId — a conditionId can't match its token comparisons,
 * so sweeping here would be a guaranteed-miss 14-sport frame spike; a miss
 * needs no retry bookkeeping either, both lookups are map reads and the
 * next render simply tries again.
 */
// Descriptions persisted BEFORE `canonicalGameKey` existed carry no key, and a
// resolved entry never re-resolves — so those rows simply don't highlight. That
// is deliberate. The obvious repair (backfill the key from the live frame on
// cache HIT) is a trap on three counts: these getters are on MyTrades' and
// PositionSidebar's render paths, so it taxes two features that have nothing to
// do with the highlight; it would make a read-shaped getter MUTATE a cached
// object those callers hold by reference, and persist to localStorage as a side
// effect of rendering; and it cannot work anyway — the cache is only ever
// consulted once the market is PRUNED, which is exactly when the live frame it
// would read is gone. The entries self-heal on their own as new markets resolve.

export function describePolyMarketCached(conditionId: string): PolyMarketDescription | null {
  if (!conditionId) return null;
  const cached = marketDescCache.get(conditionId);
  if (cached) return cached;
  const store = useMarketStore.getState();
  const ticker = store.getTickerByConditionId(conditionId);
  if (!ticker) return null;
  const mkt = store.getMarket(ticker);
  if (!mkt) return null;
  const sport = sportForMarketFrame(mkt);
  if (!sport) return null;
  const desc: PolyMarketDescription = {
    matchup: mkt.title || ticker,
    sport,
    pmTicker: ticker,
    // Captured while the market is still live — this is the ONLY moment it's
    // available for a conditionId-keyed row, and it's what lets a settled poly
    // POSITION still group by game after hydra ages the frame out.
    canonicalGameKey: mkt.canonicalGameKeyV2,
  };
  marketDescCache.set(conditionId, desc);
  persistResolvedDescs();
  return desc;
}

/**
 * CACHED variant — for the per-tick RENDER path only (row labels, card VMs,
 * sport grouping): a resolved entry serves forever (matchup/team/side/sport
 * are session-immutable; its tickSize/negRisk snapshot is display-grade —
 * anything order-shaped re-derives via the fresh variant at click time), and
 * an unresolved entry retries at most every UNRESOLVED_RETRY_MS. This sweep
 * used to run uncached per Poly order per tick from two call sites and was
 * the single worst frame spike on the positions page (~10ms per sport × 14
 * for an unresolvable id).
 */
export function describePolyOrderCached(assetId: string): PolyOrderDescription | null {
  if (!assetId) return null;
  const cached = descCache.get(assetId);
  if (cached) {
    if (cached.desc !== null) return cached.desc;
    if (performance.now() - cached.at < UNRESOLVED_RETRY_MS) return null;
  }
  return describePolyOrder(assetId);
}

function sweepForAssetId(
  assetId: string,
  store: ReturnType<typeof useMarketStore.getState>,
): PolyOrderDescription | null {
  // Iterate every sport's moneyline games — the only market type whose
  // Polymarket book carries per-team token pairs today. Spread/total and
  // series rows route their token data differently and aren't covered by
  // the trader's current Polymarket order surface.
  //
  // statusFilter/dateFilter 'all': the defaults ('active'/'today') exclude
  // FINAL and non-today games, and an order/position label must never
  // depend on the game still being live — a settled position's row would
  // otherwise miss here every UNRESOLVED_RETRY_MS and re-run this
  // 14-sport sweep every 3s, forever (the exact frame-spike class the
  // cache exists to prevent).
  const sweepFilters = { statusFilter: 'all', dateFilter: 'all' } as const;
  for (const tab of SPORT_TABS) {
    const games = store.getGames(tab.id, 'moneyline', sweepFilters);
    for (const game of games) {
      const poly = game.polymarket;
      if (!poly) continue;
      for (const teamSide of ['away', 'home'] as const) {
        const book = poly[teamSide];
        const side = matchToken(assetId, book);
        if (!side || !book) continue;
        const betTeam = teamSide === 'away' ? game.awayCode : game.homeCode;
        return {
          matchup: `${game.awayCode} @ ${game.homeCode}`,
          betTeam,
          side,
          sport: tab.id,
          date: game.date,
          tickSize: book.tickSize,
          negRisk: book.negRisk,
          pmTicker: book.marketTicker,
          // NOT GameRow.key: that is gameKeyV2(canonicalGameKeyV2 ?? legacy ??
          // PARSED), so on a keyless frame it falls through to a locally-derived
          // key — the very thing this feature must not trust — and a series row
          // yields a 6-seg key. Read the frame's v2 key directly so every
          // description in this module carries one key from one source.
          canonicalGameKey: store.getMarket(book.marketTicker)?.canonicalGameKeyV2,
        };
      }
    }
    for (const event of store.getMultiOutcomeEvents(tab.id, sweepFilters)) {
      for (const outcome of event.outcomes) {
        const desc = describeMultiOutcomeBook(assetId, tab.id, event, outcome);
        if (desc) return desc;
      }
    }
  }
  // FRAME FALLBACK — the game-row sweeps above only cover moneyline pairs
  // and soccer outcomes; spread/total (and any future market shape) tokens
  // never matched, so those orders rendered as bare hex forever (Brendan,
  // Jul-9: World Cup + spread/total orders untrackable). Every poly frame
  // hydra ships carries yes/noTokenIds, so resolve directly off the
  // ingest-time token index: venue-string labels (the market's own title)
  // instead of code-shaped matchups, but real words, real tickSize/negRisk
  // (same frame fields resolvePolyCloseTarget trusts), and a pmTicker that
  // lights up the live last/book/start plumbing.
  const frameTicker = store.getTickerByTokenId(assetId);
  if (frameTicker) {
    const mkt = store.getMarket(frameTicker);
    if (mkt?.yesTokenId && mkt?.noTokenId) {
      const side: 'yes' | 'no' = mkt.yesTokenId === assetId ? 'yes' : 'no';
      // The store's own normalization, NOT raw tab-id equality: hydra
      // tennis frames say sport='tennis' (tabs are atp/wta), soccer slugs
      // carry competition codes (fifwc), etc. — raw equality left those
      // as hex (Brendan, Jul-9).
      const sport = sportForMarketFrame(mkt);
      if (sport) {
        return {
          matchup: mkt.title || frameTicker,
          // yesOutcome (soccer/derivative frames) is the cleanest side
          // label; subtitle carries the line on spread/total frames.
          betTeam: mkt.yesOutcome || mkt.subtitle || '—',
          side,
          sport,
          date: mkt.canonicalGameKey?.split('|')[1] ?? '',
          tickSize: mkt.tickSize,
          negRisk: mkt.negRisk,
          pmTicker: frameTicker,
          // The frame carries the key outright — this is the path that makes
          // spread/total/soccer/tennis rows groupable at all: their `matchup`
          // is the market TITLE (no " @ "), so no key can be parsed out of it.
          canonicalGameKey: mkt.canonicalGameKeyV2,
        };
      }
    }
  }
  return null;
}

// ── Position → sellable token resolution ────────────────────────

export interface PolyCloseTarget {
  /** The outcome token the position actually holds — what a close SELLs. */
  tokenId: string;
  tickSize?: string;
  negRisk?: boolean;
}

/**
 * Resolve a held Polymarket position into its sellable outcome token.
 *
 * The relay's position ledger keys poly rows by the market CONDITION ID
 * (CLOB `Trade.market` — see relay/src/services/polyFillsPoller.ts), NOT
 * by tokenId; the side column says which of the market's two tokens the
 * fills bought. Placing any order requires the tokenId, so the pair is
 * mapped through the live store's token registry — the same rule
 * PositionTracker's U/P odds column uses.
 *
 * Returns null when the market isn't loaded in the store (sport tab never
 * opened, market expired). Callers MUST refuse to place and say why —
 * guessing a token or defaulting tickSize/negRisk on a live sell is how
 * money-path orders go wrong.
 */
export function resolvePolyCloseTarget(
  conditionId: string,
  side: 'yes' | 'no',
): PolyCloseTarget | null {
  if (!conditionId) return null;
  const store = useMarketStore.getState();
  // _markets is keyed by the PM- slug ticker, NOT the conditionId — the
  // direct getMarket(conditionId) this used to do was a guaranteed miss
  // (Cash Out shipped permanently disabled). Route through the store's
  // conditionId index; keep the direct read as a fallback in case a frame
  // ever keys by conditionId.
  const ticker = store.getTickerByConditionId(conditionId);
  const mkt = (ticker ? store.getMarket(ticker) : null) ?? store.getMarket(conditionId);
  if (!mkt) return null;
  const tokenId = side === 'yes' ? mkt.yesTokenId : mkt.noTokenId;
  if (!tokenId) return null;
  return { tokenId, tickSize: mkt.tickSize, negRisk: mkt.negRisk };
}

// ── Live per-order market data (Brendan, Jul 8) ─────────────────

export interface PolyOrderLiveData {
  /** Last trade price in cents for THIS order's outcome token (already
   *  inverted when the order holds the slug's NO token). null until a
   *  trade prints. */
  lastCents: number | null;
  /** Event start (ms) for the resting-order start label + start sort. */
  eventStartMs: number | null;
  /** Market traded volume. */
  volume: number | null;
  /** The order token's own bid ladder (best-bid first, cents) — feeds the
   *  queue / behind-by-N metrics. Empty when no book has streamed. */
  sideBids: BookLevel[];
}

const EMPTY_LIVE: PolyOrderLiveData = {
  lastCents: null,
  eventStartMs: null,
  volume: null,
  sideBids: [],
};

/**
 * Read the LIVE market data for a poly order, O(1) by its slug ticker —
 * no sport sweep. Separate from {@link describePolyOrder} because the
 * label resolution is cached forever (immutable) while this ticks with
 * the feed and must be read fresh every render.
 *
 * Orientation is derived from the raw slug market's own token ids
 * (yesTokenId === assetId), NOT from the away/home VenueBook split, so it
 * can't be fooled by the moneyline pair's ladder swap: the slug's `yes`
 * ladder is always the yesTokenId's bids.
 *
 * @param assetId  the order's outcome token (OpenOrder.asset_id)
 * @param pmTicker the slug ticker from the cached description
 */
export interface PolyPositionLiveData {
  /** Side-native mark for the HELD side: mid of the slug market's yes
   *  bid/ask (falling back to last trade), inverted for a NO position —
   *  the exact same recipe usePositionPnl uses for Kalshi rows, so the
   *  two venues' Now / P&L columns are comparable at a glance. */
  nowCents: number | null;
  /** Slug ticker the mark was read from; null when the market's frame
   *  hasn't been ingested (sport tab not loaded / market expired). */
  pmTicker: string | null;
}

const EMPTY_POSITION_LIVE: PolyPositionLiveData = { nowCents: null, pmTicker: null };

/**
 * Live side-native mark for a HELD poly position, keyed the way the
 * relay's position ledger keys rows: (conditionId, side). The ledger's
 * yes/no side is hydra's own orientation (yesTokenId/noTokenId — enforced
 * end-to-end by the relay's fills poller), and the slug frame's yes book
 * IS the yesTokenId's book, so `side === 'yes'` reads the yes mid
 * directly and NO inverts it.
 */
export function polyPositionLiveData(
  conditionId: string,
  side: 'yes' | 'no',
): PolyPositionLiveData {
  if (!conditionId) return EMPTY_POSITION_LIVE;
  const store = useMarketStore.getState();
  const ticker = store.getTickerByConditionId(conditionId);
  if (!ticker) return EMPTY_POSITION_LIVE;
  const yesMid = store.getMarketPrice(ticker);
  return {
    nowCents: yesMid == null ? null : side === 'yes' ? yesMid : 100 - yesMid,
    pmTicker: ticker,
  };
}

export function polyOrderLiveData(assetId: string, pmTicker: string): PolyOrderLiveData {
  if (!pmTicker) return EMPTY_LIVE;
  const store = useMarketStore.getState();
  const mkt = store.getMarket(pmTicker);
  if (!mkt) return EMPTY_LIVE;
  // Which of the slug's two tokens the order holds — orientation-proof.
  const slugSide: 'yes' | 'no' = mkt.yesTokenId === assetId ? 'yes' : 'no';
  const rawLastYes = store.getLastTradeYesCents(pmTicker);
  const lastCents =
    rawLastYes == null ? null : slugSide === 'yes' ? rawLastYes : 100 - rawLastYes;
  const raw = store.getOrderbookRaw(pmTicker);
  const rawBids = raw ? (slugSide === 'yes' ? raw.yes : raw.no) : [];
  const sideBids: BookLevel[] = rawBids.map((l) => ({ priceCents: l.price, size: l.size }));
  return {
    lastCents,
    eventStartMs: store.getEventStartTimeMsByTicker(pmTicker),
    volume: mkt.volume ?? null,
    sideBids,
  };
}
